import * as XLSX from 'xlsx';
import { apiGet } from './genesisApi/httpsMethods';
import type { Country, Deal, DealAdvisorFee, Sector, Status } from '../types/deal';
import { getKillReasonName, getLostToLenderNotes, getWinningLenderLtcLtv } from '../utils/dealStatusHelpers';
import { formatDateForExport } from '../utils/formatDate';
import type {
  FunnelBucket,
  FunnelDeal,
  FunnelGroupBy,
  FunnelOutcomes,
  FunnelStages,
  FunnelSummaryResponse,
} from '../types/dealFunnel';

interface SummaryData {
  summaries: Array<{
    name: string;
    deal_count: number;
    loan_amount_gbp: number;
    average_deal_size_gbp?: number;
  }>;
  total?: {
    deal_count: number;
    loan_amount_gbp: number;
    average_deal_size_gbp?: number;
  };
}

const formatCurrency = (value: number): string => {
  return value.toLocaleString('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });
};

const fetchSummaryData = async (groupBy: string, statusIds: number[]): Promise<SummaryData | null> => {
  let url = `deals/pipeline/summary?group_by=${groupBy}`;
  if (statusIds.length > 0) {
    const statusIdsParam = statusIds.map(id => `status_ids=${id}`).join('&');
    url += `&${statusIdsParam}`;
  }
  
  const response = await apiGet<SummaryData>(url);
  if (response.success && response.data) {
    return response.data;
  }
  return null;
};

const fetchFunnelSummary = async (
  groupBy: FunnelGroupBy,
): Promise<FunnelSummaryResponse | null> => {
  const response = await apiGet<FunnelSummaryResponse>('deals/funnel/summary', {
    group_by: groupBy,
  });
  if (response.success && response.data) {
    return response.data;
  }
  return null;
};

const volumeMmOneDecimal = (volumeGbp: number): number =>
  Math.round((volumeGbp / 1_000_000) * 10) / 10;

const pctOneDecimal = (rate: number | undefined): number | string => {
  if (rate == null || Number.isNaN(rate)) {
    return '';
  }
  return Math.round(rate * 1000) / 10;
};

function funnelRowFromStagesOutcomes(
  bucketLabel: string,
  stages: FunnelStages,
  outcomes: FunnelOutcomes,
): Record<string, string | number> {
  const s = stages;
  const o = outcomes;
  return {
    Bucket: bucketLabel,
    'Received Materials (count)': s.received_materials.count,
    'Received Materials (£MM)': volumeMmOneDecimal(s.received_materials.volume_gbp),
    'Term Sheet Submitted (count)': s.term_sheet_submitted.count,
    'Term Sheet Submitted (£MM)': volumeMmOneDecimal(s.term_sheet_submitted.volume_gbp),
    'Signed (count)': s.reached_signed.count,
    'Signed (£MM)': volumeMmOneDecimal(s.reached_signed.volume_gbp),
    'Closed (count)': Math.max(
      0,
      s.reached_closed.count - (s.closed_apollo?.count ?? 0),
    ),
    'Closed (£MM)': volumeMmOneDecimal(
      Math.max(
        0,
        s.reached_closed.volume_gbp - (s.closed_apollo?.volume_gbp ?? 0),
      ),
    ),
    'Closed - Apollo (count)': s.closed_apollo?.count ?? 0,
    'Closed - Apollo (£MM)': volumeMmOneDecimal(
      s.closed_apollo?.volume_gbp ?? 0,
    ),
    'Lost To Other Lender (count)': o.lost_to_other_lender.count,
    'Lost To Other Lender (£MM)': volumeMmOneDecimal(o.lost_to_other_lender.volume_gbp),
    'Killed After Term Sheet (count)': o.killed_after_term_sheet?.count ?? 0,
    'Killed After Term Sheet (£MM)': volumeMmOneDecimal(
      o.killed_after_term_sheet?.volume_gbp ?? 0,
    ),
    'Killed After Signing (count)': o.killed_after_signing?.count ?? 0,
    'Killed After Signing (£MM)': volumeMmOneDecimal(
      o.killed_after_signing?.volume_gbp ?? 0,
    ),
    'Term Sheet Submitted % (count)': pctOneDecimal(
      s.term_sheet_submitted.rate_count_from_received,
    ),
    'Term Sheet Submitted % (volume)': pctOneDecimal(
      s.term_sheet_submitted.rate_volume_from_received,
    ),
    'Deals Won % (count)': pctOneDecimal(s.reached_signed.rate_count_from_tss),
    'Deals Won % (volume)': pctOneDecimal(s.reached_signed.rate_volume_from_tss),
  };
}

function buildFunnelExportRows(summary: FunnelSummaryResponse): Record<string, string | number>[] {
  const rows: Record<string, string | number>[] = [];
  rows.push(
    funnelRowFromStagesOutcomes(
      'Total',
      summary.totals.stages,
      summary.totals.outcomes,
    ),
  );
  if (!summary.buckets.length) {
    return rows;
  }
  const sortedBuckets: FunnelBucket[] =
    summary.group_by === 'overall'
      ? [...summary.buckets].sort((a, b) => {
          if (a.id == null) return 1;
          if (b.id == null) return -1;
          return a.id - b.id;
        })
      : [...summary.buckets].sort(
          (a, b) =>
            b.stages.received_materials.count -
            a.stages.received_materials.count,
        );
  for (const b of sortedBuckets) {
    rows.push(funnelRowFromStagesOutcomes(b.name, b.stages, b.outcomes));
  }
  return rows;
}

const createFunnelWorksheet = (
  summary: FunnelSummaryResponse,
  sheetName: string,
  workbook: XLSX.WorkBook,
): void => {
  const rows = buildFunnelExportRows(summary);
  if (rows.length === 0) {
    return;
  }
  const worksheet = XLSX.utils.json_to_sheet(rows);
  worksheet['!cols'] = Array.from({ length: 21 }, (_, i) => ({
    wch: i === 0 ? 28 : i >= 17 ? 22 : 13,
  }));
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
};

const createWorksheet = (summary: SummaryData, sheetName: string, workbook: XLSX.WorkBook): void => {
  if (!summary || !summary.summaries) return;

  // Prepare data for export
  const rows = summary.summaries.map((item) => {
    const percentOfTotal = summary.total?.loan_amount_gbp && summary.total.loan_amount_gbp > 0
      ? (item.loan_amount_gbp / summary.total.loan_amount_gbp) * 100
      : 0;

    const nameColumn = sheetName === 'Status' ? 'Status' 
      : sheetName === 'Sector' ? 'Sector' 
      : sheetName === 'Country' ? 'Country' 
      : 'Kill Reason';

    return {
      [nameColumn]: item.name,
      'Deal Count': item.deal_count,
      'Loan Amount (GBP)': item.loan_amount_gbp,
      'Amount Formatted': formatCurrency(item.loan_amount_gbp),
      'Average Deal Size (GBP)': item.average_deal_size_gbp || 0,
      'Average Deal Size Formatted': item.average_deal_size_gbp ? formatCurrency(item.average_deal_size_gbp) : '-',
      '% of Total': Math.round(percentOfTotal * 100) / 100
    };
  });

  // Add total row
  if (summary.total) {
    const nameColumn = sheetName === 'Status' ? 'Status' 
      : sheetName === 'Sector' ? 'Sector' 
      : sheetName === 'Country' ? 'Country' 
      : 'Kill Reason';
    rows.push({
      [nameColumn]: 'Total',
      'Deal Count': summary.total.deal_count,
      'Loan Amount (GBP)': summary.total.loan_amount_gbp,
      'Amount Formatted': formatCurrency(summary.total.loan_amount_gbp),
      'Average Deal Size (GBP)': summary.total.average_deal_size_gbp || 0,
      'Average Deal Size Formatted': summary.total.average_deal_size_gbp ? formatCurrency(summary.total.average_deal_size_gbp) : '-',
      '% of Total': 100
    });
  }

  // Create worksheet
  const worksheet = XLSX.utils.json_to_sheet(rows);
  
  // Auto-size columns
  const colWidths = [
    { wch: 30 }, // Name column
    { wch: 12 }, // Deal Count
    { wch: 18 }, // Loan Amount (GBP)
    { wch: 18 }, // Amount Formatted
    { wch: 20 }, // Average Deal Size (GBP)
    { wch: 25 }, // Average Deal Size Formatted
    { wch: 12 }  // % of Total
  ];
  worksheet['!cols'] = colWidths;

  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
};

/**
 * Exports pipeline summary data to Excel format
 * @param statusIds - Array of status IDs to filter by (empty array = no filter)
 * @param statusFilter - Array of Status objects for display in export info
 * @returns Promise that resolves when export is complete
 */
export const exportSummaryToExcel = async (
  statusIds: number[],
  statusFilter: Status[]
): Promise<void> => {
  try {
    // Fetch all summary data
    const [statusData, sectorData, countryData, killReasonData] = await Promise.all([
      fetchSummaryData('status', statusIds),
      fetchSummaryData('sector', statusIds),
      fetchSummaryData('country', statusIds),
      fetchSummaryData('kill_reason', statusIds)
    ]);

    // Create workbook
    const workbook = XLSX.utils.book_new();

    // Create worksheets for each summary
    if (statusData) createWorksheet(statusData, 'Status', workbook);
    if (sectorData) createWorksheet(sectorData, 'Sector', workbook);
    if (countryData) createWorksheet(countryData, 'Country', workbook);
    if (killReasonData) createWorksheet(killReasonData, 'Kill Reason', workbook);

    // Add filter information sheet
    const filterInfo = [
      { 
        'Filter Type': 'Status Filter', 
        'Values': statusFilter.length > 0 ? statusFilter.map(s => s.status).join(', ') : 'All Statuses' 
      },
      { 
        'Filter Type': 'Export Date', 
        'Values': new Date().toLocaleString('en-GB') 
      }
    ];
    const filterWorksheet = XLSX.utils.json_to_sheet(filterInfo);
    filterWorksheet['!cols'] = [{ wch: 20 }, { wch: 50 }];
    XLSX.utils.book_append_sheet(workbook, filterWorksheet, 'Export Info');

    // Generate filename
    const datetime = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `Pipeline_Summary_${datetime}.xlsx`;

    // Write file
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    throw new Error('Failed to export to Excel. Please try again.');
  }
};

/**
 * Deal Funnel only: all group_by options (Overall, Sector, Country, Source) in one workbook.
 */
export const exportDealFunnelToExcel = async (): Promise<void> => {
  try {
    const [funnelOverall, funnelSector, funnelCountry, funnelSource] = await Promise.all([
      fetchFunnelSummary('overall'),
      fetchFunnelSummary('sector'),
      fetchFunnelSummary('country'),
      fetchFunnelSummary('source'),
    ]);

    const workbook = XLSX.utils.book_new();

    if (funnelOverall) {
      createFunnelWorksheet(funnelOverall, 'Funnel Overall', workbook);
    }
    if (funnelSector) {
      createFunnelWorksheet(funnelSector, 'Funnel Sector', workbook);
    }
    if (funnelCountry) {
      createFunnelWorksheet(funnelCountry, 'Funnel Country', workbook);
    }
    if (funnelSource) {
      createFunnelWorksheet(funnelSource, 'Funnel Source', workbook);
    }

    const infoRows = [
      {
        'Filter Type': 'Contents',
        Values: 'Deal funnel by Overall, Sector, Country, Source (same as tab options).',
      },
      {
        'Filter Type': 'Export Date',
        Values: new Date().toLocaleString('en-GB'),
      },
    ];
    const infoSheet = XLSX.utils.json_to_sheet(infoRows);
    infoSheet['!cols'] = [{ wch: 20 }, { wch: 55 }];
    XLSX.utils.book_append_sheet(workbook, infoSheet, 'Export Info');

    const datetime = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `Deal_Funnel_${datetime}.xlsx`;
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error('Error exporting deal funnel to Excel:', error);
    throw new Error('Failed to export deal funnel. Please try again.');
  }
};

export type StatusAnalysisDurationRow = {
  key: string;
  statusId: number | null;
  statusName: string;
  deals: number;
  avgDays: number;
};

export type StatusAnalysisMatrixRow = {
  dealId: number;
  dealName: string;
  statusDates: Record<string, string>;
};

export type StatusAnalysisColumn = {
  key: string;
  statusId: number | null;
  statusName: string;
};

const formatStatusAnalysisDate = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: '2-digit',
  }).format(d);
};

/**
 * Exports Status Analysis tab data (duration averages + deal matrix) to Excel.
 * Uses pre-computed client-side rows so exported data matches visible grids and filters.
 */
export const exportStatusAnalysisToExcel = (params: {
  leftRows: StatusAnalysisDurationRow[];
  dealMatrixRows: StatusAnalysisMatrixRow[];
  statusColumns: StatusAnalysisColumn[];
  statusFilter: Status[];
  selectedStatusLabel: string | null;
}): void => {
  const { leftRows, dealMatrixRows, statusColumns, statusFilter, selectedStatusLabel } =
    params;

  try {
    const workbook = XLSX.utils.book_new();

    const durationRows = leftRows.map((r) => ({
      Status: r.statusName,
      'Avg days': Math.round(r.avgDays),
      Deals: r.deals,
    }));
    const durationSheet = XLSX.utils.json_to_sheet(durationRows);
    durationSheet['!cols'] = [{ wch: 30 }, { wch: 12 }, { wch: 10 }];
    XLSX.utils.book_append_sheet(workbook, durationSheet, 'Status Duration Averages');

    const matrixHeader = ['Deal', ...statusColumns.map((c) => c.statusName)];
    const matrixBody = dealMatrixRows.map((row) => [
      row.dealName,
      ...statusColumns.map((c) => {
        const iso = row.statusDates[c.key];
        return iso ? formatStatusAnalysisDate(iso) : '';
      }),
    ]);
    const matrixSheet = XLSX.utils.aoa_to_sheet([matrixHeader, ...matrixBody]);
    matrixSheet['!cols'] = [
      { wch: 30 },
      ...statusColumns.map(() => ({ wch: 14 })),
    ];
    XLSX.utils.book_append_sheet(workbook, matrixSheet, 'Deal Status Matrix');

    const filterInfo = [
      {
        'Filter Type': 'Status Filter',
        Values:
          statusFilter.length > 0
            ? statusFilter.map((s) => s.status).join(', ')
            : 'All Statuses',
      },
      {
        'Filter Type': 'Selected Status (click filter)',
        Values: selectedStatusLabel ?? 'All',
      },
      {
        'Filter Type': 'Export Date',
        Values: new Date().toLocaleString('en-GB'),
      },
    ];
    const filterWorksheet = XLSX.utils.json_to_sheet(filterInfo);
    filterWorksheet['!cols'] = [{ wch: 28 }, { wch: 50 }];
    XLSX.utils.book_append_sheet(workbook, filterWorksheet, 'Export Info');

    const datetime = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const filename = `Status_Analysis_${datetime}.xlsx`;
    XLSX.writeFile(workbook, filename);
  } catch (error) {
    console.error('Error exporting status analysis to Excel:', error);
    throw new Error('Failed to export to Excel. Please try again.');
  }
};

const formatDealCountries = (deal: Deal): string => {
  const countries = deal.countries;
  if (!Array.isArray(countries) || countries.length === 0) return '';
  return countries
    .map((c) => (typeof c === 'object' && c != null ? c.name : String(c)))
    .filter(Boolean)
    .join(', ');
};

const exportDealGridWorkbook = (
  rows: Record<string, string | number>[],
  sheetName: string,
  filenamePrefix: string,
  filterInfo: Record<string, string>[],
  columnWidths: number[],
): void => {
  const workbook = XLSX.utils.book_new();
  const worksheet = XLSX.utils.json_to_sheet(rows);
  worksheet['!cols'] = columnWidths.map((wch) => ({ wch }));
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  const infoSheet = XLSX.utils.json_to_sheet(
    filterInfo.map((row) => ({
      'Filter Type': row['Filter Type'],
      Values: row.Values,
    })),
  );
  infoSheet['!cols'] = [{ wch: 28 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(workbook, infoSheet, 'Export Info');

  const datetime = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
  XLSX.writeFile(workbook, `${filenamePrefix}_${datetime}.xlsx`);
};

/**
 * Exports Killed Deals tab grid (visible rows) to Excel.
 */
export const exportKilledDealsToExcel = (params: {
  deals: Deal[];
  missingOnly: boolean;
}): void => {
  const { deals, missingOnly } = params;

  try {
    const rows = deals.map((deal) => ({
      'Deal Name': deal.deal_name ?? '',
      Status:
        typeof deal.status === 'object' && deal.status != null
          ? (deal.status.status ?? '')
          : '',
      'Kill Reason': getKillReasonName(deal),
      Countries: formatDealCountries(deal),
      'Loan Amount (Fully Funded)': deal.loan_amount_fully_funded ?? '',
    }));

    exportDealGridWorkbook(
      rows,
      'Killed Deals',
      'Killed_Deals',
      [
        {
          'Filter Type': 'View Filter',
          Values: missingOnly ? 'Missing kill reason only' : 'All killed deals',
        },
        {
          'Filter Type': 'Export Date',
          Values: new Date().toLocaleString('en-GB'),
        },
      ],
      [32, 22, 24, 28, 24],
    );
  } catch (error) {
    console.error('Error exporting killed deals to Excel:', error);
    throw new Error('Failed to export killed deals. Please try again.');
  }
};

/**
 * Exports Lost Deals tab grid (visible rows) to Excel.
 */
export const exportLostDealsToExcel = (params: {
  deals: Deal[];
  missingOnly: boolean;
}): void => {
  const { deals, missingOnly } = params;

  try {
    const rows = deals.map((deal) => {
      const details = deal.lost_to_lender_details;
      return {
        'Deal Name': deal.deal_name ?? '',
        'Loan Amount (Fully Funded)': deal.loan_amount_fully_funded ?? '',
        'Lost Reason': details?.lost_to_lender_reason?.reason ?? '',
        'Kill Reason': getKillReasonName(deal),
        'Winning Lender': details?.winning_lender_name ?? '',
        'Winning Loan Amount': details?.winning_lender_loan_amount ?? '',
        'Margin (%)': details?.winning_lender_margin ?? '',
        'LTC/LTV (%)': getWinningLenderLtcLtv(details) ?? '',
        'Arr. Fee (%)': details?.winning_lender_arrangement_fee ?? '',
        'Term (y)': details?.winning_lender_initial_loan_term ?? '',
        Notes: getLostToLenderNotes(details),
      };
    });

    exportDealGridWorkbook(
      rows,
      'Lost Deals',
      'Lost_Deals',
      [
        {
          'Filter Type': 'View Filter',
          Values: missingOnly
            ? 'Missing lost reason only'
            : 'All lost deals',
        },
        {
          'Filter Type': 'Export Date',
          Values: new Date().toLocaleString('en-GB'),
        },
      ],
      [32, 22, 24, 28, 24, 22, 18, 12, 12, 12, 28],
    );
  } catch (error) {
    console.error('Error exporting lost deals to Excel:', error);
    throw new Error('Failed to export lost deals. Please try again.');
  }
};

/**
 * Exports Advisor Fees summary tab grid (visible rows) to Excel.
 */
export const exportAdvisorFeesToExcel = (params: {
  fees: DealAdvisorFee[];
  statusFilter: Status[];
  countryFilter: Country[];
  sectorFilter: Sector[];
}): void => {
  const { fees, statusFilter, countryFilter, sectorFilter } = params;

  try {
    const rows = fees.map((fee) => ({
      Deal: fee.deal?.deal_name ?? (fee.deal_id != null ? `Deal ${fee.deal_id}` : ''),
      'Deal ID': fee.deal_id ?? fee.deal?.id ?? '',
      Status: fee.deal?.status?.status ?? '',
      'Add-On': fee.accordion_name ?? '',
      Service: fee.service ?? '',
      Firm: fee.firm ?? '',
      Task: fee.task ?? '',
      Currency: fee.currency ?? '',
      'Quote Amount': fee.quote_amount ?? '',
      'Actual Amount': fee.actual_amount ?? '',
      'Billable Hours Total': fee.billable_hours_total_amount ?? '',
    }));

    exportDealGridWorkbook(
      rows,
      'Advisor Fees',
      'Advisor_Fees',
      [
        {
          'Filter Type': 'Status',
          Values:
            statusFilter.length > 0
              ? statusFilter.map((s) => s.status).join(', ')
              : 'All',
        },
        {
          'Filter Type': 'Countries',
          Values:
            countryFilter.length > 0
              ? countryFilter
                  .map((c) => c.nicename ?? c.name)
                  .filter(Boolean)
                  .join(', ')
              : 'All',
        },
        {
          'Filter Type': 'Sectors',
          Values:
            sectorFilter.length > 0
              ? sectorFilter.map((s) => s.sector).join(', ')
              : 'All',
        },
        {
          'Filter Type': 'Export Date',
          Values: new Date().toLocaleString('en-GB'),
        },
      ],
      [32, 10, 18, 18, 24, 28, 12, 14, 14, 18],
    );
  } catch (error) {
    console.error('Error exporting advisor fees to Excel:', error);
    throw new Error('Failed to export advisor fees. Please try again.');
  }
};

/** Maps funnel drill-down deals to the same fields shown in the side panel. */
export function funnelDrillDealsToExcelRows(
  deals: FunnelDeal[],
): Record<string, string | number>[] {
  return deals.map((deal) => ({
    'Deal Name': deal.deal_name?.trim() || `Deal ${deal.id}`,
    Status: deal.current_status?.name ?? 'No status',
    Country: deal.primary_country?.name ?? '',
    Sector: deal.primary_sector?.name ?? '',
    Source: deal.source?.name ?? 'No source',
    'Loan Amount (Fully Funded)': deal.loan_amount_fully_funded ?? '',
    Currency: deal.loan_amount_currency ?? '',
    'Loan Amount (GBP)': Number.isFinite(deal.loan_amount_gbp)
      ? deal.loan_amount_gbp
      : '',
    Created: formatDateForExport(deal.created_date),
  }));
}

/**
 * Exports the deal funnel cell drill-down (visible side-panel deals) to Excel.
 */
export const exportFunnelDrillDealsToExcel = (params: {
  deals: FunnelDeal[];
  sliceTitle: string;
}): void => {
  const { deals, sliceTitle } = params;

  try {
    exportDealGridWorkbook(
      funnelDrillDealsToExcelRows(deals),
      'Deals',
      'Deal_Funnel_Deals',
      [
        {
          'Filter Type': 'Slice',
          Values: sliceTitle || 'Deal funnel',
        },
        {
          'Filter Type': 'Export Date',
          Values: new Date().toLocaleString('en-GB'),
        },
      ],
      [36, 20, 18, 18, 18, 24, 10, 20, 14],
    );
  } catch (error) {
    console.error('Error exporting funnel deals to Excel:', error);
    throw new Error('Failed to export funnel deals. Please try again.');
  }
};

